import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { useResetPasswordMutation } from "../toolkitServices/userApi";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { HiEye, HiEyeOff } from "react-icons/hi";

const ResetPassword = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    try {
      await resetPassword({
        phone: state.phone,  // ✅ from VerifyOtp page
        otp: data.otp,
        newPassword: data.password,
      }).unwrap();

      toast.success("Password reset successfully!");
      navigate("/login");
    } catch (err) {
      toast.error(err?.data?.msg || "Failed to reset password");
    }
  };

  return (
    <div className="flex items-center justify-center h-screen bg-[#f9f4e8]">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-white shadow-lg rounded-xl p-8 w-96"
      >
        <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
          Reset Password
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* OTP */}
          <div>
            <label className="text-sm text-gray-700">Enter OTP</label>
            <input
              {...register("otp", { required: "OTP is required" })}
              type="text"
              placeholder="Enter OTP received on phone"
              className={`w-full border ${
                errors.otp ? "border-red-400" : "border-gray-300"
              } rounded px-3 py-2 focus:ring-2 focus:ring-[#7d70b2]`}
            />
            {errors.otp && (
              <p className="text-red-500 text-xs mt-1">{errors.otp.message}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label className="text-sm text-gray-700">New Password</label>
            <div className="relative">
              <input
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Minimum 6 characters required",
                  },
                })}
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                className={`w-full border ${
                  errors.password ? "border-red-400" : "border-gray-300"
                } rounded px-3 py-2 focus:ring-2 focus:ring-[#7d70b2]`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? (
                  <HiEyeOff className="text-gray-500" />
                ) : (
                  <HiEye className="text-gray-500" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="text-sm text-gray-700">Confirm Password</label>
            <div className="relative">
              <input
                {...register("confirmPassword", {
                  required: "Confirm password is required",
                  validate: (value) =>
                    value === watch("password") || "Passwords do not match",
                })}
                type={showConfirm ? "text" : "password"}
                placeholder="Re-enter new password"
                className={`w-full border ${
                  errors.confirmPassword ? "border-red-400" : "border-gray-300"
                } rounded px-3 py-2 focus:ring-2 focus:ring-[#7d70b2]`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((prev) => !prev)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showConfirm ? (
                  <HiEyeOff className="text-gray-500" />
                ) : (
                  <HiEye className="text-gray-500" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            disabled={isLoading}
            type="submit"
            className="w-full bg-[#7d70b2] hover:bg-[#6e63a5] text-white py-2 rounded-md transition"
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
